import { Injectable } from '@angular/core';
import { Observable, from, combineLatest, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { 
  Firestore, 
  collection, 
  getDocs,
  query,
  where,
  orderBy,
  limit
} from '@angular/fire/firestore';

export interface MetricsTreeData {
  eventType: string;
  name: string;
  value: number;
  category?: string;
  subcategory?: string;
}

export interface DashboardMetrics {
  users: {
    total: number;
    active: number;
    created: number;
    updated: number;
    deleted: number;
  };
  cards: {
    total: number;
    active: number;
    created: number;
    updated: number;
    deleted: number;
  };
  bookmarks: {
    total: number;
    created: number;
    updated: number;
    deleted: number;
  };
  lastUpdated: Date;
}

export interface MetricEntry {
  name: string;
  value: number;
  eventType: string;
  operation: string;
  category?: string;
  subcategory?: string;
  updatedAt?: any;
}

export interface DetailedMetrics {
  overall: {
    create: MetricEntry[];
    update: MetricEntry[];
    delete: MetricEntry[];
  };
  monthly: {
    [monthKey: string]: {
      create: MetricEntry[];
      update: MetricEntry[];
      delete: MetricEntry[];
    };
  };
  yearly: {
    [year: string]: {
      create: MetricEntry[];
      update: MetricEntry[];
      delete: MetricEntry[];
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class MetricsService {

  constructor(private firestore: Firestore) {
    console.log('📊 Metrics service initialized');
  }

  /**
   * Get all detailed metrics from the Firestore paths
   * /metrics/digitalcard/create
   * /metrics/digitalcard/delete  
   * /metrics/digitalcard/update
   */
  getDetailedMetrics(): Observable<DetailedMetrics> {
    console.log('🔍 Fetching detailed metrics from all Firestore paths...');
    
    return from(this.fetchAllMetricsData()).pipe(
      catchError(error => {
        console.error('❌ Error fetching detailed metrics:', error);
        return of({
          overall: { create: [], update: [], delete: [] },
          monthly: {},
          yearly: {}
        });
      })
    );
  }

  /**
   * Fetch all metrics data from Firestore paths
   */
  private async fetchAllMetricsData(): Promise<DetailedMetrics> {
    try {
      const result: DetailedMetrics = {
        overall: { create: [], update: [], delete: [] },
        monthly: {},
        yearly: {}
      };

      // Fetch overall metrics
      for (const operation of ['create', 'update', 'delete']) {
        const overallPath = `metrics/digitalcard/${operation}`;
        const entries = await this.fetchMetricsFromPath(overallPath);
        result.overall[operation as keyof typeof result.overall] = entries;
      }

      // Fetch monthly metrics for current and past months
      const currentDate = new Date();
      for (let i = 0; i < 12; i++) { // Last 12 months
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth() + 1}`;
        
        result.monthly[monthKey] = {
          create: [],
          update: [],
          delete: []
        };

        for (const operation of ['create', 'update', 'delete']) {
          const monthlyPath = `metrics/digitalcard/${operation}/monthly/${monthKey}`;
          const entries = await this.fetchMetricsFromPath(monthlyPath);
          result.monthly[monthKey][operation as keyof typeof result.monthly[string]] = entries;
        }
      }

      // Fetch yearly metrics for current and past years
      for (let i = 0; i < 3; i++) { // Last 3 years
        const year = (currentDate.getFullYear() - i).toString();
        
        result.yearly[year] = {
          create: [],
          update: [],
          delete: []
        };

        for (const operation of ['create', 'update', 'delete']) {
          const yearlyPath = `metrics/digitalcard/${operation}/yearly/${year}`;
          const entries = await this.fetchMetricsFromPath(yearlyPath);
          result.yearly[year][operation as keyof typeof result.yearly[string]] = entries;
        }
      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching all metrics data:', error);
      return {
        overall: { create: [], update: [], delete: [] },
        monthly: {},
        yearly: {}
      };
    }
  }

  /**
   * Fetch metrics from a specific Firestore path
   */
  private async fetchMetricsFromPath(path: string): Promise<MetricEntry[]> {
    try {
      console.log(`🔍 Fetching metrics from path: ${path}`);
      
      const metricsCollection = collection(this.firestore, path);
      const snapshot = await getDocs(metricsCollection);
      
      const entries: MetricEntry[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        entries.push({
          name: data['name'] || doc.id,
          value: data['value'] || 0,
          eventType: data['eventType'] || 'unknown',
          operation: data['operation'] || 'unknown',
          category: data['category'],
          subcategory: data['subcategory'],
          updatedAt: data['updatedAt']
        });
      });

      console.log(`✅ Found ${entries.length} entries in ${path}`);
      return entries;
    } catch (error) {
      console.warn(`⚠️ Could not fetch metrics from path ${path}:`, error);
      return [];
    }
  }

  /**
   * Get aggregated summary of all metrics
   */
  getMetricsSummary(): Observable<any> {
    return this.getDetailedMetrics().pipe(
      map(detailedMetrics => {
        const summary = {
          cards: { created: 0, updated: 0, deleted: 0, total: 0 },
          users: { created: 0, updated: 0, deleted: 0, total: 0 },
          bookmarks: { created: 0, updated: 0, deleted: 0, total: 0 },
          overall: { created: 0, updated: 0, deleted: 0, total: 0 }
        };

        // Aggregate from overall metrics
        for (const operation of ['create', 'update', 'delete'] as const) {
          const entries = detailedMetrics.overall[operation];
          
          entries.forEach(entry => {
            const value = entry.value || 0;
            summary.overall[operation === 'delete' ? 'deleted' : `${operation}d` as keyof typeof summary.overall] += value;
            
            // Categorize by collection type
            if (entry.name === 'cards' || entry.category === 'digitalcard') {
              summary.cards[operation === 'delete' ? 'deleted' : `${operation}d` as keyof typeof summary.cards] += value;
            } else if (entry.name === 'users') {
              summary.users[operation === 'delete' ? 'deleted' : `${operation}d` as keyof typeof summary.users] += value;
            } else if (entry.name === 'bookmarks') {
              summary.bookmarks[operation === 'delete' ? 'deleted' : `${operation}d` as keyof typeof summary.bookmarks] += value;
            }
          });
        }

        // Calculate totals (created - deleted)
        summary.cards.total = Math.max(0, summary.cards.created - summary.cards.deleted);
        summary.users.total = Math.max(0, summary.users.created - summary.users.deleted);
        summary.bookmarks.total = Math.max(0, summary.bookmarks.created - summary.bookmarks.deleted);
        summary.overall.total = Math.max(0, summary.overall.created - summary.overall.deleted);

        return summary;
      })
    );
  }

  /**
   * Get dashboard metrics from the cloud function processed data
   */
  getDashboardMetrics(): Observable<DashboardMetrics> {
    console.log('🔍 Fetching dashboard metrics from cloud function data...');
    
    return this.getMetricsSummary().pipe(
      map(summary => {
        console.log('📊 Metrics summary received:', summary);
        
        return {
          users: {
            total: summary.users.total,
            active: summary.users.total, // Assume all users are active for now
            created: summary.users.created,
            updated: summary.users.updated,
            deleted: summary.users.deleted
          },
          cards: {
            total: summary.cards.total,
            active: summary.cards.total, // Assume all cards are active for now
            created: summary.cards.created,
            updated: summary.cards.updated,
            deleted: summary.cards.deleted
          },
          bookmarks: {
            total: summary.bookmarks.total,
            created: summary.bookmarks.created,
            updated: summary.bookmarks.updated,
            deleted: summary.bookmarks.deleted
          },
          lastUpdated: new Date()
        };
      }),
      catchError(error => {
        console.error('❌ Error fetching dashboard metrics:', error);
        return of({
          users: { total: 0, active: 0, created: 0, updated: 0, deleted: 0 },
          cards: { total: 0, active: 0, created: 0, updated: 0, deleted: 0 },
          bookmarks: { total: 0, created: 0, updated: 0, deleted: 0 },
          lastUpdated: new Date()
        });
      })
    );
  }

  /**
   * Get metrics tree data for UI display
   */
  getMetricsTreeData(operationType: string, timeRange: string): Observable<MetricsTreeData[]> {
    return from(this.fetchMetricsTreeData(operationType, timeRange)).pipe(
      catchError(error => {
        console.error('❌ Error fetching metrics tree data:', error);
        return of([]);
      })
    );
  }

  /**
   * Fetch metrics tree data from Firestore
   */
  private async fetchMetricsTreeData(operationType: string, timeRange: string): Promise<MetricsTreeData[]> {
    try {
      let basePath = `metrics/digitalcard/${operationType}`;
      
      if (timeRange === 'this_month') {
        const now = new Date();
        const yearMonth = `${now.getFullYear()}-${now.getMonth() + 1}`;
        basePath += `/monthly/${yearMonth}`;
      } else if (timeRange === 'last_month') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const yearMonth = `${lastMonth.getFullYear()}-${lastMonth.getMonth() + 1}`;
        basePath += `/monthly/${yearMonth}`;
      } else if (timeRange === 'this_year') {
        const now = new Date();
        basePath += `/yearly/${now.getFullYear()}`;
      }

      const metricsCollection = collection(this.firestore, basePath);
      const snapshot = await getDocs(metricsCollection);
      
      const metrics: MetricsTreeData[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        metrics.push({
          eventType: data['eventType'] || 'eventby4',
          name: data['name'] || doc.id,
          value: data['value'] || 0,
          category: data['category'],
          subcategory: data['subcategory']
        });
      });

      return metrics;
    } catch (error) {
      console.error('❌ Error fetching metrics tree data:', error);
      return [];
    }
  }
}
